import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Phone, Save, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function OwnerProfileEdit() {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", { name, phone });
      setUser(data);
      localStorage.setItem("hn_user", JSON.stringify(data));
      toast.success("Profile updated · listings now show your new phone");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  if (!user) return null;
  return (
    <section className="mt-10">
      <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Your account</div>
      <h2 className="font-display font-black text-3xl tracking-tighter mt-1 flex items-center gap-2">
        <User size={26} className="text-orange-800" /> Profile
      </h2>

      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-6">
        {!editing ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Name</div>
                <div data-testid="owner-profile-name" className="mt-1 font-display font-bold text-lg">{user.name}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Phone</div>
                <div data-testid="owner-profile-phone" className="mt-1 font-mono text-lg">{user.phone || "—"}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Email</div>
                <div className="mt-1 text-sm text-slate-700">{user.email} <span className="text-[10px] text-slate-400">(email can't be changed — contact support)</span></div>
              </div>
            </div>
            <Button
              data-testid="owner-edit-profile-btn"
              onClick={() => { setName(user.name); setPhone(user.phone || ""); setEditing(true); }}
              variant="outline"
              className="rounded-full gap-1 h-10 self-start sm:self-auto"
            >
              <Pencil size={14} /> Edit
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="prof-name">Name</Label>
              <Input id="prof-name" data-testid="owner-profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11" />
            </div>
            <div>
              <Label htmlFor="prof-phone">Phone (10 digits)</Label>
              <div className="mt-1 relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="prof-phone" data-testid="owner-profile-phone-input" inputMode="numeric" maxLength={10} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} className="h-11 pl-9" placeholder="9876543210" />
              </div>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2 pt-2 border-t border-slate-100">
              <Button
                data-testid="owner-profile-save"
                onClick={save}
                disabled={saving || (!name.trim() && !phone.trim())}
                className="rounded-full bg-orange-800 hover:bg-orange-900 text-white gap-1"
              >
                <Save size={14} /> {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} disabled={saving} className="rounded-full">Cancel</Button>
              <div className="ml-auto text-[11px] text-slate-500">Updating your phone auto-updates it on all your hostel listings so students can reach you.</div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
